<template>
  <v-card
    :height="widgetHeight"
    :min-width="widgetWidth"
    class="ma-0 pa-0"
    :loading="loading"
    @mouseover.native="hover = true"
    @mouseleave.native="hover = false"
  >
    <v-card-title
      v-if="!loading"
      class="pa-0 ma-0"
      style="max-height: 40px !important"
    >
      <widget-header
        :title="
          documentTrackerSettings.title || $t('WidgetDocumentTrackerTitle')
        "
        :color="documentTrackerSettings.color || '#607d8b'"
        icon="mdi-calendar-month"
      >
        <template slot="actions">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                v-show="hover"
                @click.prevent="refresh"
                >mdi-refresh</v-icon
              >
            </template>
            <span>{{ $t("Refresh") }}</span>
          </v-tooltip>
          <v-tooltip top v-if="enableEdit">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                v-show="hover"
                @click.prevent="editSettings"
                >mdi-cog</v-icon
              >
            </template>
            <span>{{ $t("Settings") }}</span>
          </v-tooltip>
          <v-tooltip top v-if="enableEdit">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                v-show="hover"
                @click.prevent="removeWidget"
                >mdi-delete</v-icon
              >
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </template>
      </widget-header>
    </v-card-title>
    <v-card-text
      class="pa-3 ma-0"
      :style="`height: ${contentHeight}px;max-height: ${contentHeight}px`"
    >
      <v-row dense>
        <v-col @click="showData('Expired')">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-hover v-slot="{ hover }">
                  <MiniStatistic
                    :elevation="hover ? 4 : 2"
                    :class="hover ? 'pointer' : ''"
                    icon="mdi-calendar-alert"
                    :title="expiredDocumentsList.length.toString()"
                    sub-title=" "
                    :color="documentTrackerSettings.expiredColor"
                    :textColor="documentTrackerSettings.expiredTextColor"
                    iconSize="48"
                  />
                </v-hover>
              </div>
            </template>
            <span>{{ $t("ExpiredPlural") }}</span>
          </v-tooltip>
        </v-col>
        <v-col @click="showData('Warning')">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-hover v-slot="{ hover }">
                  <MiniStatistic
                    :elevation="hover ? 4 : 2"
                    :class="hover ? 'pointer' : ''"
                    icon="mdi-calendar-clock"
                    :title="warningDocumentsList.length.toString()"
                    sub-title=" "
                    :color="documentTrackerSettings.warningColor"
                    :textColor="documentTrackerSettings.warningTextColor"
                    iconSize="48"
                  />
                </v-hover>
              </div>
            </template>
            <span>{{
              `${$t("ExpireInLessThan")} ${
                documentTrackerSettings.warningDays
              } ${$t("Days")}`
            }}</span>
          </v-tooltip>
        </v-col>
        <v-col @click="showData('OnTime')">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-hover v-slot="{ hover }">
                  <MiniStatistic
                    :elevation="hover ? 4 : 2"
                    :class="hover ? 'pointer' : ''"
                    icon="mdi-calendar-check"
                    :title="okDocumentsList.length.toString()"
                    sub-title=" "
                    :color="documentTrackerSettings.onTimeColor"
                    :textColor="documentTrackerSettings.onTimeTextColor"
                    iconSize="48"
                  />
                </v-hover>
              </div>
            </template>
            <span>{{ $t("Ok") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <SettingsModal :ref="settingsModalKey" @on-save="onSettingsSave" />
      <DataModal
        ref="modalTrackerDataKey"
        :widgetTitle="
          documentTrackerSettings.title || $t('WidgetDocumentTrackerTitle')
        "
        @on-documents-validated="updateData(true)"
        @on-documents-updated="updateData"
        :warningDays="documentTrackerSettings.warningDays"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import SettingsModal from "@/components/widgets/documentTracker/WidgetDocumentTrackerSettings";
import DocumentTrackerSettings from "@/ecm/models/widget/documentTracker/DocumentTrackerSettings";
import DataModal from "@/components/widgets/documentTracker/WidgetDocumentTrackerDataModal";
import DocumentTrackerData from "@/ecm/models/widget/documentTracker/DocumentTrackerData";
import WidgetHeader from "@/components/widgets/WidgetHeader";
import MiniStatistic from "@/components/widgets/statistic/MiniStatistic";
import WidgetService from "@/services/WidgetService";
import { mapGetters } from "vuex";
import Utils from "@/ecm/common/Utils";

export default {
  components: {
    SettingsModal,
    WidgetHeader,
    MiniStatistic,
    DataModal,
  },
  props: {
    widgetId: {
      type: Number,
      default: 0,
    },
    id: {
      type: Number,
      default: 0,
    },
    enableEdit: {
      type: Boolean,
      default: false,
    },
    height: { type: Number, default: 0 },
    width: { type: Number, default: 0 },
    settings: { type: Object },
  },
  mounted() {
    this.widgetHeight = this.height;
    this.widgetWidth = this.width;
    this.documentTrackerSettings = new DocumentTrackerSettings(this.settings);
    this.refresh();

    this.connection = $.hubConnection(this.apiUrl);
    this.connection.qs = { access_token: this.token };
    var notificationHub = this.connection.createHubProxy("notificationHub");
    let self = this;
    notificationHub.on("refresh", (notifications) => {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(self.refresh, 5000);
    });
    this.connection.start();
  },
  destroyed() {
    if (this.connection) {
      this.connection.stop();
      this.connection = null;
    }
  },
  data() {
    return {
      connection: null,
      hover: false,
      headerHeight: 40,
      widgetHeight: 0,
      widgetWidth: 0,
      loading: false,
      documentTrackerSettings: new DocumentTrackerSettings(),
      settingsModalKey: "modalSettingsKey",
      dbTableName: "widgetDocumentTracker",
      expiredDocumentsList: [],
      warningDocumentsList: [],
      okDocumentsList: [],
    };
  },
  computed: {
    ...mapGetters({
      user: "account/user",
      apiUrl: "account/apiUrl",
      token: "account/token",
      userContentTypes: "account/userContentTypes",
    }),
    modalSettings() {
      return this.$refs[this.settingsModalKey];
    },
    dataToDisplay() {
      var fieldsToShow = this.documentTrackerSettings.fieldsToShow;
      var expiredData = this.expiredDocumentsList.map(
        (d) => new DocumentTrackerData(d, "Expired", fieldsToShow)
      );
      var warningData = this.warningDocumentsList.map(
        (d) => new DocumentTrackerData(d, "Warning", fieldsToShow)
      );
      var onTimeData = this.okDocumentsList.map(
        (d) => new DocumentTrackerData(d, "OnTime", fieldsToShow)
      );
      return [...expiredData, ...warningData, ...onTimeData];
    },
    contentHeight() {
      return this.widgetHeight - this.headerHeight;
    },
  },
  methods: {
    resize(height, width) {
      this.widgetHeight = height;
      this.widgetWidth = width;
    },
    editSettings() {
      this.modalSettings.edit(this.documentTrackerSettings);
    },
    showData(state, refreshData) {
      var setttings = Utils.object.toJS(this.documentTrackerSettings);
      this.$refs["modalTrackerDataKey"].showData(
        this.dataToDisplay,
        state,
        refreshData,
        setttings
      );
    },
    onSettingsSave(settings) {
      var widgetDto = {
        Id: this.id,
        WidgetID: this.widgetId,
        Settings: settings.toDto(),
      };
      var self = this;
      WidgetService.updateSettings(widgetDto).then((data) => {
        self.documentTrackerSettings = settings;
        self.refresh();
      });
    },
    removeWidget() {
      this.$emit("on-remove-widget", this.id);
    },
    refresh() {
      this.loading = true;
      this.getData();
    },
    async updateData(keepDataModalOpen) {
      await this.getData();
      if (keepDataModalOpen) {
        this.showData("", true);
      }
    },
    async getData() {
      var data = await WidgetService.getData({
        WidgetID: this.widgetId,
        Settings: this.documentTrackerSettings.toDto(),
      });
      this.loading = false;
      this.expiredDocumentsList = data.ExpiredDocuments;
      this.warningDocumentsList = data.WarningDocuments;
      this.okDocumentsList = data.OkDocuments;
    },
  },
};
</script>
<style>
.pointer {
  cursor: pointer;
}
</style>